<template>
    <v-container class="px-5">
        <v-row>
            <v-col>
                <h4>CRM Integrations</h4>
                <p class="text-caption text-grey">
                    Signup leads pushed to the CRM so sales can call new users back. Enable or disable each system
                    and check its connection. Credentials live in server configuration and are never shown here.
                </p>
            </v-col>
            <v-col cols="auto" class="d-flex align-center">
                <v-btn size="small" variant="text" prepend-icon="mdi-refresh" :loading="loading" @click="fetchSystems">
                    Refresh
                </v-btn>
            </v-col>
        </v-row>

        <v-row>
            <v-col v-for="sys in systems" :key="sys.portal" cols="12" md="6">
                <v-card variant="outlined" :loading="busy[sys.portal]">
                    <v-card-title class="d-flex align-center justify-space-between">
                        <span>{{ sys.label }}</span>
                        <v-chip size="small" variant="tonal" :color="statusColor(sys)">
                            <v-icon start size="14">{{ statusIcon(sys) }}</v-icon>
                            {{ statusText(sys) }}
                        </v-chip>
                    </v-card-title>

                    <v-card-text>
                        <v-table density="compact" class="border rounded mb-3">
                            <tbody>
                                <tr><td class="font-weight-medium" width="160">Product</td><td>{{ sys.product || '-' }}</td></tr>
                                <tr><td class="font-weight-medium">Queue</td><td><code>{{ sys.queue || '-' }}</code></td></tr>
                                <tr><td class="font-weight-medium">Integration ID</td><td class="text-caption" style="word-break:break-all;">{{ sys.integration_id || '-' }}</td></tr>
                                <tr><td class="font-weight-medium">Key ID</td><td class="text-caption" style="word-break:break-all;">{{ sys.key_id || '-' }}</td></tr>
                                <tr>
                                    <td class="font-weight-medium">Signing secret</td>
                                    <td>
                                        <v-icon :color="sys.signing_key_set ? 'success' : 'error'" size="16">
                                            {{ sys.signing_key_set ? 'mdi-check-circle' : 'mdi-close-circle' }}
                                        </v-icon>
                                        {{ sys.signing_key_set ? 'Set' : 'Missing' }}
                                    </td>
                                </tr>
                                <tr>
                                    <td class="font-weight-medium">Configured</td>
                                    <td>{{ sys.configured ? 'Yes' : 'No (set env keys)' }}</td>
                                </tr>
                            </tbody>
                        </v-table>

                        <div class="d-flex align-center mb-3">
                            <v-switch
                                :model-value="sys.enabled"
                                :disabled="!sys.configured || busy[sys.portal]"
                                color="primary" density="compact" hide-details inset
                                :label="sys.enabled ? 'Enabled' : 'Disabled'"
                                @update:model-value="val => toggle(sys, val)"/>
                            <v-spacer/>
                            <v-btn size="small" variant="tonal" prepend-icon="mdi-lan-connect"
                                   :loading="checking[sys.portal]" :disabled="!sys.configured"
                                   @click="check(sys)">
                                Check status
                            </v-btn>
                        </div>

                        <v-alert v-if="sys.health" :type="sys.health.ok ? 'success' : 'error'"
                                 density="compact" variant="tonal" class="mb-3">
                            {{ sys.health.message }}<span v-if="sys.health.status"> (HTTP {{ sys.health.status }})</span>
                        </v-alert>

                        <div class="text-caption text-grey">
                            <span class="mr-3">Pending: {{ sys.stats?.pending ?? 0 }}</span>
                            <span class="mr-3">Delivered (24h): {{ sys.stats?.delivered_24h ?? 0 }}</span>
                            <span class="mr-3">Failed: {{ sys.stats?.failed_total ?? 0 }}</span>
                        </div>
                        <div v-if="sys.stats?.last_event_id" class="text-caption text-grey mt-1" style="word-break:break-all;">
                            Last: {{ sys.stats.last_event_id }}
                            <span v-if="sys.stats.last_status"> &middot; HTTP {{ sys.stats.last_status }}</span>
                            <span v-if="sys.stats.last_detail"> &middot; {{ sys.stats.last_detail }}</span>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup>
import {onMounted, reactive, ref} from 'vue'
import {useRouter} from 'vue-router'
import {send} from '@/composables/apiClient'
import {useSnackbarStore} from '@/stores/snackBarStore'
import {useAuthStore} from '@/stores/authStore'

const router = useRouter()
const authStore = useAuthStore()
const snackbar = useSnackbarStore()

const systems = ref([])
const loading = ref(false)
const busy = reactive({})      // portal -> toggling
const checking = reactive({})  // portal -> status check in flight

const fetchSystems = async () => {
    loading.value = true
    try {
        const res = await send('get', 'admin/crm-integrations')
        systems.value = (res.data?.data ?? []).map(s => ({...s, health: null}))
    } catch (e) {
        snackbar.error?.('Failed to load CRM integrations')
    } finally {
        loading.value = false
    }
}

const toggle = async (sys, val) => {
    busy[sys.portal] = true
    try {
        const res = await send('post', 'admin/crm-integrations/toggle', {portal: sys.portal, enabled: val})
        Object.assign(sys, res.data?.data ?? {}, {health: sys.health})
        snackbar.success?.(val ? `${sys.label} enabled` : `${sys.label} disabled`)
    } catch (e) {
        snackbar.error?.('Could not change the integration')
        await fetchSystems() // resync the switch to the server truth
    } finally {
        busy[sys.portal] = false
    }
}

const check = async (sys) => {
    checking[sys.portal] = true
    sys.health = null
    try {
        const res = await send('post', 'admin/crm-integrations/check', {portal: sys.portal})
        sys.health = res.data?.data ?? {ok: false, message: 'No response'}
    } catch (e) {
        sys.health = {ok: false, message: 'Status check failed', status: e?.response?.status ?? null}
    } finally {
        checking[sys.portal] = false
    }
}

const statusText = (s) => !s.configured ? 'Not configured' : (s.active ? 'Active' : 'Disabled')
const statusColor = (s) => !s.configured ? 'grey' : (s.active ? 'success' : 'warning')
const statusIcon = (s) => !s.configured ? 'mdi-cog-off' : (s.active ? 'mdi-check-circle' : 'mdi-pause-circle')

onMounted(() => {
    // Super-admin only; the API enforces the same, this just avoids a dead screen.
    if (!authStore.user?.super) {
        router.replace('/users')
        return
    }
    fetchSystems()
})
</script>
